import React from 'react';
import { Link } from 'react-router-dom';

import {
    ArrowRight,
    Shield,
    Cloud,
    Monitor,
    Server,
    Cpu,
    Network,
    Headphones,
    Settings,
    Bot
} from 'lucide-react';
import './ModernServices.scss';

// Subtle SVG decorations for each service card — monoline style, positioned bottom-right
const cardSvgs = {
    primary: ( // Cybersecurity — shield outline
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 4L6 12v12c0 11.1 7.7 21.5 18 24 10.3-2.5 18-12.9 18-24V12L24 4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M17 24l5 5 9-9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    ),
    blue: ( // Cloud — cloud with connection dots
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 36h22a9 9 0 002-17.8A11 11 0 0016 18a9 9 0 00-3 18z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            <circle cx="24" cy="28" r="2" fill="currentColor"/>
            <circle cx="17" cy="30" r="1.5" fill="currentColor"/>
            <circle cx="31" cy="30" r="1.5" fill="currentColor"/>
        </svg>
    ),
    cyan: ( // IT Modernization — monitor with rising arrow
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="6" y="8" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M18 40h12M24 32v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M16 26l8-8 4 4 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M32 14h4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    ),
    purple: ( // Hardware — server rack
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="10" y="6" width="28" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <rect x="10" y="22" width="28" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="16" cy="12" r="2" fill="currentColor"/>
            <circle cx="16" cy="28" r="2" fill="currentColor"/>
            <path d="M24 12h8M24 28h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M20 38h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M24 34v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
    ),
    orange: ( // Software Licensing — file with key
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 6h16l8 8v28a2 2 0 01-2 2H14a2 2 0 01-2-2V6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M28 6v8h8" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            <circle cx="24" cy="28" r="4" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M24 32v6M22 35h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
    ),
    green: ( // Network — connected nodes
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="4" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="10" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="38" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="10" cy="36" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="38" cy="36" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M13 14l8 8M35 14l-8 8M13 34l8-8M35 34l-8-8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
    ),
    red: ( // IT Support — headset with sound waves
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 26V22a14 14 0 0128 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <rect x="6" y="26" width="6" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <rect x="36" y="26" width="6" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M42 34v2a4 4 0 01-4 4h-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="28" cy="40" r="2" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
    ),
    indigo: ( // Digital Transformation — cog with circuit lines
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 6v4M24 38v4M6 24h4M38 24h4M11.2 11.2l2.8 2.8M34 34l2.8 2.8M36.8 11.2L34 14M14 34l-2.8 2.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="24" cy="24" r="8" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="24" cy="24" r="3" fill="currentColor"/>
        </svg>
    ),
};

const ModernServices = () => {
    const services = [
        {
            icon: <Bot size={28} />,
            title: 'AI Automation & Workflows',
            description: 'Spending too much time on repetitive tasks? We build internal and external automations, AI copilots inside your apps, and AI social media content workflows in Make and n8n, deployed on AWS.',
            link: '/ai-automation',
            color: 'indigo',
            featured: true
        },
        {
            icon: <Server size={28} />,
            title: 'Hardware Solutions',
            description: 'Enterprise-grade hardware from leading manufacturers at competitive prices.',
            link: '/accessories',
            color: 'purple'
        },
        {
            icon: <Cpu size={28} />,
            title: 'Software Licensing',
            description: 'Authorized software licensing and subscription management services.',
            link: '/service',
            color: 'orange'
        },
        {
            icon: <Monitor size={28} />,
            title: 'IT Modernization',
            description: 'Transform legacy systems with cutting-edge technology and best practices.',
            link: '/it-modernization',
            color: 'cyan'
        },
        {
            icon: <Headphones size={28} />,
            title: 'IT Support',
            description: '24/7 technical support and managed services to keep your operations running.',
            link: '/service',
            color: 'red'
        },
        {
            icon: <Network size={28} />,
            title: 'Network Infrastructure',
            description: 'Design, implement, and maintain robust network solutions for your organization.',
            link: '/service',
            color: 'green'
        },
        {
            icon: <Cloud size={28} />,
            title: 'Cloud Solutions',
            description: 'Scalable cloud infrastructure and migration services for modern businesses.',
            link: '/digital',
            color: 'blue'
        },
        {
            icon: <Settings size={28} />,
            title: 'Digital Transformation',
            description: 'End-to-end digital transformation consulting and implementation.',
            link: '/digital',
            color: 'indigo'
        },
        {
            icon: <Shield size={28} />,
            title: 'Cybersecurity',
            description: 'Comprehensive security solutions to protect your organization from evolving threats.',
            link: '/cybersecurity',
            color: 'primary'
        }
    ];

    return (
        <section className="jk-services">
            <div className="jk-services__container">
                {/* Header */}
                <div className="jk-services__header">
                    <span className="jk-section-label">
                        <Settings size={16} />
                        Our Services
                    </span>
                    <h2 className="jk-services__title">
                        Technology Solutions for
                        <span className="jk-services__title-highlight"> Every Need</span>
                    </h2>
                    <p className="jk-services__subtitle">
                        From AI automation to hardware procurement and complete digital transformation,
                        we provide comprehensive IT solutions tailored to your requirements.
                    </p>
                </div>

                {/* Services Grid */}
                <div className="jk-services__grid">
                    {services.map((service, index) => (
                        <Link
                            key={index}
                            to={service.link}
                            className={`jk-services__card jk-services__card--${service.color}${service.featured ? ' jk-services__card--featured' : ''}`}
                        >
                            <div className="jk-services__card-icon">
                                {service.icon}
                            </div>
                            <h3 className="jk-services__card-title">{service.title}</h3>
                            <p className="jk-services__card-description">{service.description}</p>
                            <span className="jk-services__card-link">
                                Learn More
                                <ArrowRight size={16} />
                            </span>
                            <span className="jk-services__card-decor" aria-hidden="true">
                                {cardSvgs[service.color]}
                            </span>
                        </Link>
                    ))}
                </div>

                {/* CTA */}
                <div className="jk-services__cta">
                    <p className="jk-services__cta-text">
                        Need a custom solution? Let's discuss your requirements.
                    </p>
                    <Link to="/contact" className="jk-btn jk-btn--primary">
                        Contact Our Experts
                        <ArrowRight size={20} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default ModernServices;
